import {
  React, memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { hotelSearch } from '../../../../store/slices/hotel';
import SortingFilter from '../../../Filters/SortingFilter';
import HotelPagination from '../HotelPagination';
import Map from '../../Map';
import HotelFilterBar from '../HotelFilterBar';
import HotelItinerary from '../HotelItinerary';
import HotelList from '../HotelList';

import {
  FiltersInitialValue, PageLinks, SearchModes, SearchStatus,
} from '../../../../constants';
import useHotelSortingValues from '../../../../hooks/useHotelSortingValues';
import PollingSearchProgressBar from '../../../PollingSearchProgressBar';
import PassengersWarningMessage from '../PassengersWarningMessage';

const HotelsModalContent = ({
  tripId, hotelCheckInDate,
  hotelCheckOutDate, handleOpenModal, handleCloseModal,
  onModalClose,
  onSortChange,
  defaultSearchLocationName,
  selectedHotelLabel,
  travelOption,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const hotelSearchState = useSelector((store) => store.hotel.hotelSearches[tripId]);
  const trip = useSelector((store) => store.trip.selectedTrips[tripId]);
  const sortingValues = useHotelSortingValues(tripId);
  const [hotelToScroll, setHotelToScroll] = useState({ hotelKey: '' });
  const [hoveredHotelKey, setHoveredHotelKey] = useState(null);
  const hotelSearchStatus = hotelSearchState && hotelSearchState.searchStatus;

  const { pageName } = router.query;
  const isChangeHotelPage = pageName === PageLinks.ChangeHotel.pageName;

  const handleMarkerClick = (hotelKey, pageNumber) => {
    setHotelToScroll({ hotelKey });
    const { page } = hotelSearchState.searchObj;
    if (page !== pageNumber) {
      dispatch(hotelSearch({
        tripId,
        searchObj: {
          page: pageNumber,
        },
      }));
    }
  };

  const handleHotelItineraryMouseEnter = useCallback((hotelKey) => {
    setHoveredHotelKey(hotelKey);
  }, [setHoveredHotelKey]);

  const handleHotelItineraryMouseLeave = useCallback(() => {
    setHoveredHotelKey(null);
  }, [setHoveredHotelKey]);

  const selectedHotelItineraries = useMemo(() => {
    if (!trip || !trip.hotelItineraries) return [];
    const hotelItineraries = Array.isArray(trip.hotelItineraries)
      ? trip.hotelItineraries
      : Object.values(trip.hotelItineraries);

    if (!hotelItineraries || !hotelItineraries.length) return [];

    return hotelItineraries.reduce((acc, current) => {
      if (!acc.some((hotel) => hotel.hotelKey === current.hotelKey)) {
        acc.push(current);
      }
      return acc;
    }, []);
  }, [trip]);

  useEffect(() => {
    if (hotelToScroll && hotelToScroll.value && hotelSearchStatus === SearchStatus.DONE) {
      const timeout = setTimeout(() => {
        setHotelToScroll({ hotelKey: '' });
      }, 3000);
      return () => clearTimeout(timeout);
    }
    return () => ({});
  }, [hotelToScroll, hotelSearchStatus]);

  return (
    <>
      <PollingSearchProgressBar tripId={tripId} searchMode={SearchModes.HOTEL} />
      <div className="flex justify-center">
        <div className="p-5 flex-shrink flex-grow">
          {trip && trip.hotelItineraries
          && (Object.keys(trip.hotelItineraries).length > 0) && !isChangeHotelPage ? (
            <>
              <div className="mb-5 font-bold">
                {selectedHotelLabel}
              </div>
              {selectedHotelItineraries.map((itinerary) => (
                <HotelItinerary
                  id={itinerary.id}
                  key={itinerary.id}
                  itinerary={itinerary}
                  tripId={tripId}
                  onModalClose={onModalClose}
                  isInsideModal
                  isExpanded={hotelToScroll.hotelKey === itinerary.hotel.hotelKey}
                  onMouseEnter={handleHotelItineraryMouseEnter}
                  onMouseLeave={handleHotelItineraryMouseLeave}
                  isHotelSelected
                  travelOption={travelOption}
                />
              ))}
            </>
            )
            : ''}
          <div className="font-bold flex justify-between items-center">
            All hotels (
            {hotelCheckInDate}
            {' '}
            -
            {' '}
            {hotelCheckOutDate}
            )
            {hotelSearchState && (
              <SortingFilter
                sortingValues={sortingValues}
                initialValue={FiltersInitialValue.hotel.sortBy}
                value={hotelSearchState.searchObj.sortBy}
                direction={hotelSearchState.searchObj.sortDirection}
                onSortChange={onSortChange}
                variant="transparentOutline"
              />
            )}
          </div>
          <HotelPagination tripId={tripId} className="mb-5" />
          <HotelFilterBar
            defaultSearchLocationName={defaultSearchLocationName}
            tripId={tripId}
          />
          <PassengersWarningMessage className="mb-3" />
          <HotelList
            onViewMoreClick={handleOpenModal}
            isInsideModal
            tripId={tripId}
            onModalClose={handleCloseModal}
            hotelToScroll={hotelToScroll}
            onHotelItineraryMouseEnter={handleHotelItineraryMouseEnter}
            onHotelItineraryMouseLeave={handleHotelItineraryMouseLeave}
            doExcludeSelectedHotel={!isChangeHotelPage}
            travelOption={travelOption}
          />
          <div className="mb-5">
            <HotelPagination tripId={tripId} />
          </div>
        </div>
        <Map
          tripId={tripId}
          onMarkerClick={handleMarkerClick}
          hoveredMarkerKey={hoveredHotelKey}
        />
      </div>
    </>
  );
};

HotelsModalContent.propTypes = {
  tripId: PropTypes.string.isRequired,
  hotelCheckInDate: PropTypes.string.isRequired,
  hotelCheckOutDate: PropTypes.string.isRequired,
  handleOpenModal: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  defaultSearchLocationName: PropTypes.string.isRequired,
  selectedHotelLabel: PropTypes.string,
  travelOption: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.number,
  }),
};

HotelsModalContent.defaultProps = {
  selectedHotelLabel: 'Selected hotel',
  travelOption: undefined,
};

export default memo(HotelsModalContent);
